// Paso 3 del asistente (PE-A3b): cálculos en vivo. Solo pinta y edita ENTRADAS; los resultados salen de
// src/lib/pe/pe-paso3.ts. Los datos generales y los sectores se muestran de solo lectura con su origen.
// PE-C1: cada capítulo muestra solo sus bloques (bloques); las entradas son una sola y "Guardar cálculos" guarda todas.

import React from 'react';
import { CONDICIONES_PUNTO_ENCUENTRO, etiquetaNivel, PELIGROS_PUNTO_ENCUENTRO, Probabilidad, Consecuencia, Nivel4 } from '../../lib/pe/pe-constantes-plan-referencia';
import { FACTORES_X, FactorX, MEDIOS_Y, MedioY } from '../../lib/pe/pe-constantes-meseri';
import { FCA_RTQ5, NIVELES_RIESGO_RTQ3, NivelRiesgoRtq3, OcupacionFCA } from '../../lib/pe/pe-constantes-normativa';
import type { FilaEvaluacion } from '../../lib/pe/pe-datos';
import {
  amenazaDeZona, ContextoPaso3, EntradaAmenazaPaso3, EntradaSectorVerificacion, EntradasPaso3, rociadoresDe, esQuitoCodigo, Evaluacion, fcaSugerida, UnidadMeseri, unidadesMeseri,
} from '../../lib/pe/pe-paso3';
import { filaDe } from '../../lib/pe/pe-paso3-guardado';
import { conteo } from '../../lib/pe/pe-resultados';
import { TOGAWA_POR_DEFECTO } from '../../lib/pe/pe-evacuacion';
import { cargaTermicaPuntos, distanciaBomberosPuntos } from '../../lib/pe/pe-meseri';
import { formatoEc, formatoTiempo } from '../../lib/pe/pe-unidades';
import { criteriosDe, ETIQUETA_DIMENSION, respuestaDesdeVerificacion, Respuesta } from '../../lib/pe/pe-vulnerabilidad';
import { Campo, NumeroValor, Semaforo, TrazaItem } from './ComponentesPE';
import { plural } from '../../lib/plurales';

const ETIQUETA_FCA: Record<OcupacionFCA, string> = {
  industrial: 'Industrial', oficinas: 'Oficinas', almacenamiento: 'Almacenamiento', residencial: 'Residencial', alojamiento: 'Alojamiento',
  aulas: 'Aulas', talleres_laboratorios: 'Talleres y laboratorios', guarderias: 'Guarderías', salud_tratamiento: 'Salud (tratamiento)',
  mercantil_planta_baja: 'Mercantil (planta baja)', mercantil_sobre_planta_baja: 'Mercantil (sobre planta baja)',
};
const NIVELES: { v: Nivel4; t: string }[] = [{ v: 'bajo', t: 'Baja' }, { v: 'medio', t: 'Media' }, { v: 'alto', t: 'Alta' }, { v: 'muy_alto', t: 'Muy alta' }];
const RESPUESTAS: { v: Respuesta; t: string }[] = [
  { v: 'cumple', t: 'Cumple (1)' }, { v: 'parcial', t: 'Parcial (0,5)' }, { v: 'no_cumple', t: 'No cumple (0)' }, { v: 'no_aplica', t: 'No aplica' },
];
const ETIQUETA_AMENAZA: Record<string, string> = { volcan: 'Volcán', inundacion: 'Inundación', otra: 'Otra amenaza' };

export interface PropsPaso3 {
  contexto: ContextoPaso3;
  entradas: EntradasPaso3;
  setEntradas: (e: EntradasPaso3) => void;
  evaluaciones: Evaluacion[];
  filas: FilaEvaluacion[];
  cambiadas: string[];
  ro: boolean;
  puedeBorrar: boolean;
  guardando: boolean;
  onGuardar: () => void;
  onBorrarEvaluacion: (fila: FilaEvaluacion, titulo: string) => void;
  irAPaso: (p: number, claveEvaluacion?: string) => void;
  // PE-C1: claves (o prefijos) de las evaluaciones que se muestran; sin esto, todas (como el paso 3 de antes).
  bloques?: string[];
  sinGuardar?: boolean;   // el botón "Guardar cálculos" va en el último bloque del capítulo
  sinNota?: boolean;
  destino?: (paso: number, claveEvaluacion?: string) => string;
}

function Tarjeta({ ev, filas, cambiadas, puedeBorrar, onBorrar, irAPaso, destino, children }: {
  ev: Evaluacion | undefined; filas: FilaEvaluacion[]; cambiadas: string[]; puedeBorrar: boolean;
  onBorrar: (f: FilaEvaluacion, t: string) => void; irAPaso: (p: number, clave?: string) => void; destino?: (p: number, clave?: string) => string;
  children: React.ReactNode;
}) {
  if (!ev) return null;
  const c = conteo(ev.trazas);
  const fila = filaDe(ev, filas);
  return (
    <section className="pe2-card" aria-label={ev.titulo}>
      <div className="pe2-cabecera">
        <h3>{ev.titulo}</h3>
        <div className="pe2-chips">
          {(['no_cumple', 'por_validar', 'cumple'] as const).filter(e => c[e]).map(e => <span key={e}><Semaforo estado={e} /> {c[e]}</span>)}
        </div>
      </div>
      {cambiadas.includes(ev.clave) && (
        <div className="pe2-aviso" role="status">El resultado cambió desde la última vez que se guardó. Revisa y vuelve a guardar.</div>
      )}
      {children}
      <details className="pe2-detalles">
        <summary>Trazas del cálculo ({ev.trazas.length})</summary>
        <div className="pe2-lista-trazas">{ev.trazas.map((t, i) => <TrazaItem key={i} t={t} irAPaso={irAPaso} claveEvaluacion={ev.clave} destino={destino} />)}</div>
      </details>
      {fila && puedeBorrar && (
        <div className="pe2-acciones">
          <button type="button" className="pe2-btn peligro" onClick={() => onBorrar(fila, ev.titulo)}>Borrar evaluación guardada</button>
        </div>
      )}
    </section>
  );
}

export default function Paso3Calculos(p: PropsPaso3) {
  const { contexto: c, entradas: e, setEntradas, evaluaciones, ro } = p;
  const ev = (clave: string) => evaluaciones.find(x => x.clave === clave);
  const muestra = (clave: string) => !p.bloques || p.bloques.some(b => clave.startsWith(b));
  const tarjeta = (clave: string, children: React.ReactNode) => (muestra(clave) ? (
    <Tarjeta key={clave} ev={ev(clave)} filas={p.filas} cambiadas={p.cambiadas} puedeBorrar={p.puedeBorrar}
      onBorrar={p.onBorrarEvaluacion} irAPaso={p.irAPaso} destino={p.destino}>{children}</Tarjeta>
  ) : null);
  const quito = esQuitoCodigo(c);
  const aforoEv = ev('aforo_egreso');
  const aforo = typeof aforoEv?.resumen.aforo === 'number' ? aforoEv.resumen.aforo : null;
  const verificacion = ev('verificacion');

  const setMeseri = (u: UnidadMeseri, cambio: Partial<EntradasPaso3['meseri'][string]>) => {
    const actual = e.meseri[u.clave] ?? { factores: {}, medios: {} };
    setEntradas({ ...e, meseri: { ...e.meseri, [u.clave]: { ...actual, ...cambio } } });
  };
  const setAmenaza = (i: number, cambio: Partial<EntradaAmenazaPaso3>) =>
    setEntradas({ ...e, amenazas: e.amenazas.map((a, j) => (j === i ? { ...a, ...cambio } : a)) });

  return (
    <div className="pe2-columna">
      {!p.sinNota && <p className="pe2-nota" style={{ margin: 0 }}>
        Todo se recalcula mientras escribes. Los datos generales y los sectores se usan tal cual (de solo lectura); los totales no se escriben a mano.
      </p>}

      {/* ── MESERI ── */}
      {muestra('meseri') && unidadesMeseri(c.sectores).length === 0 && <div className="pe2-card">MESERI: primero crea los sectores (capítulo 2, sectores y carga de fuego).</div>}
      {unidadesMeseri(c.sectores).map(u => {
        const m = e.meseri[u.clave] ?? { factores: {}, medios: {} };
        const evM = ev(`meseri:${u.clave}`);
        const qs = typeof evM?.resumen.qsMJm2 === 'number' ? evM.resumen.qsMJm2 : null;
        const brigadaPorDefecto = c.brigadaMiembros !== null ? c.brigadaMiembros > 0 : false;
        return tarjeta(`meseri:${u.clave}`, (
          <>
            <div className="pe2-grid">
              <Campo etiqueta="Carga térmica (de los sectores)" nota="Solo lectura: sale del Qs calculado en Sectores y carga de fuego.">
                <input type="text" disabled readOnly value={qs === null ? 'Falta el Qs (Sectores y carga de fuego)'
                  : `${formatoEc(qs, 1)} MJ/m² = ${formatoEc(cargaTermicaPuntos(qs).mcalM2, 1)} Mcal/m² → ${cargaTermicaPuntos(qs).puntos} puntos`} />
              </Campo>
              <Campo etiqueta="Distancia a bomberos (de los datos generales)" nota="Se puntúan los dos y se toma el menos favorable.">
                <input type="text" disabled readOnly value={c.bomberosKm === null || c.bomberosMin === null ? 'Falta en los datos generales (capítulo 1)'
                  : `${formatoEc(c.bomberosKm, 2)} km y ${formatoEc(c.bomberosMin, 1)} min → ${distanciaBomberosPuntos(c.bomberosKm, c.bomberosMin)} puntos`} />
              </Campo>
            </div>
            <h4>Factores X</h4>
            <div className="pe2-grid">
              {(Object.keys(FACTORES_X) as FactorX[]).filter(f => f !== 'distancia_bomberos' && f !== 'carga_termica').map(f => (
                <Campo key={f} etiqueta={`${FACTORES_X[f].grupo} · ${FACTORES_X[f].etiqueta}`}>
                  <select value={m.factores[f] ?? ''} disabled={ro} onChange={ev2 => setMeseri(u, { factores: { ...m.factores, [f]: ev2.target.value || undefined } })}>
                    <option value="">Elige…</option>
                    {FACTORES_X[f].opciones.map(o => <option key={o.clave} value={o.clave}>{o.etiqueta} ({o.puntos})</option>)}
                  </select>
                </Campo>
              ))}
            </div>
            <h4>Factores Y (medios de protección)</h4>
            <div className="pe2-grid">
              {(Object.keys(MEDIOS_Y) as MedioY[]).map(md => (
                <Campo key={md} etiqueta={MEDIOS_Y[md].etiqueta}>
                  <select value={m.medios[md] ?? 'no'} disabled={ro} onChange={ev2 => setMeseri(u, { medios: { ...m.medios, [md]: ev2.target.value as 'no' | 'sv' | 'cv' } })}>
                    <option value="no">No tiene (0)</option>
                    <option value="sv">Sin vigilancia ({MEDIOS_Y[md].sv})</option>
                    <option value="cv">Con vigilancia ({MEDIOS_Y[md].cv})</option>
                  </select>
                </Campo>
              ))}
            </div>
            <label className="pe2-check">
              <input type="checkbox" disabled={ro} checked={m.brigada ?? brigadaPorDefecto} onChange={ev2 => setMeseri(u, { brigada: ev2.target.checked })} />
              Brigada (B = 1){c.brigadaMiembros !== null ? ` · ${plural(c.brigadaMiembros, 'persona', 'personas')} en la brigada` : ''}
            </label>
            {evM?.resumen.p !== undefined && (
              <div className="pe2-res">
                <div><b>P = {formatoEc(evM.resumen.p as number, 2)}</b><span>5X/129 + 5Y/26 + B (X {String(evM.resumen.x)}, Y {String(evM.resumen.y)}, B {String(evM.resumen.b)})</span></div>
                <div><b>Riesgo {String(evM.resumen.cualitativa)}</b><span>Escala de MESERI (aceptable si P &gt; 5)</span></div>
              </div>
            )}
          </>
        ));
      })}

      {/* ── Aforo y anchos ── */}
      {tarjeta('aforo_egreso', (
        <>
          {!quito && <div className="pe2-nota">La RTQ 5 solo aplica en el Distrito Metropolitano de Quito: aquí los resultados son de referencia (informativos).</div>}
          <div className="pe2-nota">
            Edificación (datos generales): {c.edificacion === 'nueva' ? 'nueva → Tabla 3 de la RTQ 5' : c.edificacion === 'existente' ? 'existente → Tabla 4 de la RTQ 5' : 'sin indicar: el ancho mínimo de escaleras queda por validar'}.
          </div>
          <div className="pe2-grid">
            <Campo etiqueta="Uso (RTQ 5, Tabla 1)" nota={`Sugerido desde la ocupación de los datos generales. Área: ${c.areaM2 === null ? 'falta (capítulo 1)' : `${formatoEc(c.areaM2, 2)} m²`}`}>
              <select value={e.aforo.ocupacionFca ?? fcaSugerida(c.ocupacion) ?? ''} disabled={ro}
                onChange={x => setEntradas({ ...e, aforo: { ...e.aforo, ocupacionFca: (x.target.value || null) as OcupacionFCA | null } })}>
                <option value="">Elige…</option>
                {(Object.keys(FCA_RTQ5) as OcupacionFCA[]).map(o => (
                  <option key={o} value={o}>{ETIQUETA_FCA[o]} ({FCA_RTQ5[o].fca === null ? 'sin FCA' : `${formatoEc(FCA_RTQ5[o].fca as number, 1)} m²/persona`})</option>
                ))}
              </select>
            </Campo>
            {(e.aforo.ocupacionFca ?? fcaSugerida(c.ocupacion)) === 'almacenamiento' && (
              <Campo etiqueta="Ocupantes (la Tabla 1 no da FCA)">
                <NumeroValor valor={e.aforo.ocupantes} unidad="personas" entero disabled={ro}
                  onChange={n => setEntradas({ ...e, aforo: { ...e.aforo, ocupantes: n } })} />
              </Campo>
            )}
            <label className="pe2-check">
              <input type="checkbox" disabled={ro} checked={!!e.aforo.altoRiesgo} onChange={x => setEntradas({ ...e, aforo: { ...e.aforo, altoRiesgo: x.target.checked } })} />
              Alto riesgo (10 mm por persona en puertas y 18 en escaleras)
            </label>
          </div>
          {aforo !== null && <p className="pe2-nota">Aforo: {aforo} personas (redondeado hacia arriba).</p>}
          <h4>Salidas medidas</h4>
          {e.aforo.salidas.map((s, i) => (
            <div key={i} className="pe2-fila">
              <Campo etiqueta="Nombre"><input type="text" value={s.nombre} disabled={ro}
                onChange={x => setEntradas({ ...e, aforo: { ...e.aforo, salidas: e.aforo.salidas.map((y, j) => (j === i ? { ...y, nombre: x.target.value } : y)) } })} /></Campo>
              <Campo etiqueta="Tipo">
                <select value={s.tipo} disabled={ro}
                  onChange={x => setEntradas({ ...e, aforo: { ...e.aforo, salidas: e.aforo.salidas.map((y, j) => (j === i ? { ...y, tipo: x.target.value as 'puerta' | 'escalera' } : y)) } })}>
                  <option value="puerta">Puerta</option><option value="escalera">Escalera</option>
                </select>
              </Campo>
              <Campo etiqueta="Ancho medido">
                <NumeroValor valor={s.anchoM} unidad="m" disabled={ro}
                  onChange={n => setEntradas({ ...e, aforo: { ...e.aforo, salidas: e.aforo.salidas.map((y, j) => (j === i ? { ...y, anchoM: n ?? 0 } : y)) } })} />
              </Campo>
              {s.tipo === 'escalera' ? (
                <Campo etiqueta="Sector" nota="Con ocupantes en el sector, la escalera se calcula con ellos (RTQ 5, 5.2.2).">
                  <select value={s.sectorId ?? ''} disabled={ro}
                    onChange={x => setEntradas({ ...e, aforo: { ...e.aforo, salidas: e.aforo.salidas.map((y, j) => (j === i ? { ...y, sectorId: x.target.value || null } : y)) } })}>
                    <option value="">Sin sector (aforo total)</option>
                    {c.sectores.filter(x => x.sector.id).map(x => (
                      <option key={x.sector.id} value={x.sector.id}>{x.sector.name}{x.sector.occupants != null ? ` (${x.sector.occupants} ocupantes)` : ' (sin ocupantes)'}</option>
                    ))}
                  </select>
                </Campo>
              ) : <div />}
              <div>{!ro && <button type="button" className="pe2-btn peligro" onClick={() => setEntradas({ ...e, aforo: { ...e.aforo, salidas: e.aforo.salidas.filter((_, j) => j !== i) } })}>Quitar</button>}</div>
            </div>
          ))}
          {!ro && <div><button type="button" className="pe2-btn" onClick={() => setEntradas({ ...e, aforo: { ...e.aforo, salidas: e.aforo.salidas.concat({ nombre: `Salida ${e.aforo.salidas.length + 1}`, tipo: 'puerta', anchoM: 0.9 }) } })}>Agregar salida</button></div>}
        </>
      ))}

      {/* ── Togawa y RSET ── */}
      {tarjeta('rset', (
        <>
        <div className="pe2-grid">
          <Campo etiqueta="Personas (N)" nota={`Si lo dejas vacío: ${aforo ?? c.trabajadores ?? '—'} (aforo o trabajadores).`}>
            <NumeroValor valor={e.evacuacion.personas} unidad="personas" entero disabled={ro} onChange={n => setEntradas({ ...e, evacuacion: { ...e.evacuacion, personas: n } })} />
          </Campo>
          <Campo etiqueta="Ancho de la salida (A)"><NumeroValor valor={e.evacuacion.anchoM} unidad="m" disabled={ro} onChange={n => setEntradas({ ...e, evacuacion: { ...e.evacuacion, anchoM: n } })} /></Campo>
          <Campo etiqueta="Distancia del recorrido (D)"><NumeroValor valor={e.evacuacion.distanciaM} unidad="m" disabled={ro} onChange={n => setEntradas({ ...e, evacuacion: { ...e.evacuacion, distanciaM: n } })} /></Campo>
          <Campo etiqueta="K (por defecto 1,3)" nota="Editable; si lo cambias, la traza lo dice.">
            <NumeroValor valor={e.evacuacion.k ?? TOGAWA_POR_DEFECTO.k} unidad="personas/(m·s)" disabled={ro} onChange={n => setEntradas({ ...e, evacuacion: { ...e.evacuacion, k: n } })} />
          </Campo>
          <Campo etiqueta="V (por defecto 0,6)">
            <NumeroValor valor={e.evacuacion.v ?? TOGAWA_POR_DEFECTO.v} unidad="m/s" disabled={ro} onChange={n => setEntradas({ ...e, evacuacion: { ...e.evacuacion, v: n } })} />
          </Campo>
          <Campo etiqueta="Detección"><NumeroValor valor={e.evacuacion.deteccionS} unidad="s" disabled={ro} onChange={n => setEntradas({ ...e, evacuacion: { ...e.evacuacion, deteccionS: n } })} /></Campo>
          <Campo etiqueta="Alarma"><NumeroValor valor={e.evacuacion.alarmaS} unidad="s" disabled={ro} onChange={n => setEntradas({ ...e, evacuacion: { ...e.evacuacion, alarmaS: n } })} /></Campo>
          <Campo etiqueta="Premovimiento" nota="Sin valor por defecto: lo define el responsable y se ajusta con los simulacros.">
            <NumeroValor valor={e.evacuacion.premovimientoS} unidad="s" disabled={ro} onChange={n => setEntradas({ ...e, evacuacion: { ...e.evacuacion, premovimientoS: n } })} />
          </Campo>
        </div>
        {typeof ev('rset')?.resumen.tsSegundos === 'number' && (
          <div className="pe2-res">
            <div><b>TS = {formatoTiempo(ev('rset')!.resumen.tsSegundos as number)}</b><span>Togawa: N/(A·K) + D/V</span></div>
            {typeof ev('rset')?.resumen.rsetSegundos === 'number' && (
              <div><b>RSET = {formatoTiempo(ev('rset')!.resumen.rsetSegundos as number)}</b><span>Detección + alarma + premovimiento + TS</span></div>
            )}
          </div>
        )}
        </>
      ))}

      {/* ── Punto de encuentro ── */}
      {tarjeta('punto_encuentro', (
        <>
          <div className="pe2-grid">
            <Campo etiqueta="m² por persona" nota="Lo define el responsable."><NumeroValor valor={e.punto.m2PorPersona} unidad="m²" disabled={ro} onChange={n => setEntradas({ ...e, punto: { ...e.punto, m2PorPersona: n } })} /></Campo>
            <Campo etiqueta="Área disponible"><NumeroValor valor={e.punto.areaDisponibleM2} unidad="m²" disabled={ro} onChange={n => setEntradas({ ...e, punto: { ...e.punto, areaDisponibleM2: n } })} /></Campo>
            <Campo etiqueta="Distancia a la edificación"><NumeroValor valor={e.punto.distanciaM} unidad="m" disabled={ro} onChange={n => setEntradas({ ...e, punto: { ...e.punto, distanciaM: n } })} /></Campo>
            <Campo etiqueta="Distancia mínima" nota="La define el responsable."><NumeroValor valor={e.punto.distanciaMinimaM} unidad="m" disabled={ro} onChange={n => setEntradas({ ...e, punto: { ...e.punto, distanciaMinimaM: n } })} /></Campo>
          </div>
          <h4>Condiciones (todas eliminatorias)</h4>
          <div className="pe2-grid">
            {CONDICIONES_PUNTO_ENCUENTRO.map(cd => (
              <Campo key={cd.clave} etiqueta={cd.etiqueta}>
                <select value={e.punto.condiciones[cd.clave] === undefined ? '' : e.punto.condiciones[cd.clave] ? 'si' : 'no'} disabled={ro}
                  onChange={x => setEntradas({ ...e, punto: { ...e.punto, condiciones: { ...e.punto.condiciones, [cd.clave]: x.target.value === '' ? undefined : x.target.value === 'si' } } })}>
                  <option value="">Sin responder</option><option value="si">Sí</option><option value="no">No</option>
                </select>
              </Campo>
            ))}
          </div>
          <h4>Peligros (probabilidad × consecuencia)</h4>
          {PELIGROS_PUNTO_ENCUENTRO.map(pg => {
            const v = e.punto.peligros[pg.clave];
            const set = (cambio: Partial<{ p: Probabilidad; c: Consecuencia }> | null) => setEntradas({
              ...e, punto: { ...e.punto, peligros: { ...e.punto.peligros, [pg.clave]: cambio === null ? undefined : { p: 'B', c: 'LD', ...v, ...cambio } } },
            });
            return (
              <div key={pg.clave} className="pe2-fila">
                <div className="ancho pe2-nota">{pg.etiqueta}</div>
                <Campo etiqueta="Probabilidad">
                  <select value={v?.p ?? ''} disabled={ro} onChange={x => set(x.target.value ? { p: x.target.value as Probabilidad } : null)}>
                    <option value="">No evaluado</option><option value="B">Baja</option><option value="M">Media</option><option value="A">Alta</option>
                  </select>
                </Campo>
                <Campo etiqueta="Consecuencia">
                  <select value={v?.c ?? ''} disabled={ro || !v} onChange={x => set({ c: x.target.value as Consecuencia })}>
                    <option value="LD">Ligeramente dañino</option><option value="D">Dañino</option><option value="ED">Extremadamente dañino</option>
                  </select>
                </Campo>
              </div>
            );
          })}
        </>
      ))}

      {/* ── Verificación normativa ── */}
      {tarjeta('verificacion', (
        <>
          <p className="pe2-nota" style={{ margin: 0 }}>
            {quito ? 'Distrito Metropolitano de Quito: AM 1257 y RTQ 2026; en cada regla gana la más exigente.' : 'Fuera del DMQ: AM 1257.'}{' '}
            Brigada: {c.brigadaMiembros === null ? 'no se pudo leer' : `${plural(c.brigadaMiembros, 'persona registrada', 'personas registradas')} en la brigada`}.
          </p>
          <div className="pe2-grid">
            <Campo etiqueta="Distancia máxima a un extintor" nota={quito ? 'Máximo 15 m (RTQ 7, 15.4)' : 'Máximo 20 m (AM 1257, Art. 120)'}>
              <NumeroValor valor={e.verificacion.distanciaMaxExtintorM} unidad="m" disabled={ro} onChange={n => setEntradas({ ...e, verificacion: { ...e.verificacion, distanciaMaxExtintorM: n } })} />
            </Campo>
            <Campo etiqueta="Iluminación de emergencia: duración" nota="Mínimo 60 min (AM 1257, Art. 23)">
              <NumeroValor valor={e.verificacion.iluminacion?.duracionMin} unidad="min" disabled={ro} onChange={n => setEntradas({ ...e, verificacion: { ...e.verificacion, iluminacion: { ...e.verificacion.iluminacion, duracionMin: n } } })} />
            </Campo>
            <Campo etiqueta="Iluminancia promedio" nota="Mínimo 10 lux">
              <NumeroValor valor={e.verificacion.iluminacion?.luxPromedio} unidad="lux" disabled={ro} onChange={n => setEntradas({ ...e, verificacion: { ...e.verificacion, iluminacion: { ...e.verificacion.iluminacion, luxPromedio: n } } })} />
            </Campo>
            <Campo etiqueta="Iluminancia mínima" nota="Mínimo 1 lux">
              <NumeroValor valor={e.verificacion.iluminacion?.luxMinimo} unidad="lux" disabled={ro} onChange={n => setEntradas({ ...e, verificacion: { ...e.verificacion, iluminacion: { ...e.verificacion.iluminacion, luxMinimo: n } } })} />
            </Campo>
            {quito && (
              <Campo etiqueta="Separación entre luces de emergencia" nota="Máximo 12 m (RTQ 5, 9.2.1)">
                <NumeroValor valor={e.verificacion.iluminacion?.separacionMaxM} unidad="m" disabled={ro} onChange={n => setEntradas({ ...e, verificacion: { ...e.verificacion, iluminacion: { ...e.verificacion.iluminacion, separacionMaxM: n } } })} />
              </Campo>
            )}
            <Campo etiqueta="Fecha de la última prueba hidrostática">
              <input type="date" value={e.verificacion.fechaHidrostatica ?? ''} disabled={ro}
                onChange={x => setEntradas({ ...e, verificacion: { ...e.verificacion, fechaHidrostatica: x.target.value || null } })} />
            </Campo>
            <Campo etiqueta="Intervalo de la prueba hidrostática" nota="Por defecto 6 años (AM 1257, Art. 32 d); editable.">
              <NumeroValor valor={e.verificacion.intervaloHidrostaticaAnos ?? 6} unidad="años" disabled={ro} onChange={n => setEntradas({ ...e, verificacion: { ...e.verificacion, intervaloHidrostaticaAnos: n } })} />
            </Campo>
          </div>
          {c.sectores.some(s => s.sector.id) && <h4>Por sector</h4>}
          {c.sectores.filter(s => s.sector.id).map(s => {
            const id = s.sector.id as string;
            const v = e.verificacion.porSector?.[id] ?? {};
            const set = (cambio: EntradaSectorVerificacion) => setEntradas({
              ...e, verificacion: { ...e.verificacion, porSector: { ...e.verificacion.porSector, [id]: { ...v, ...cambio } } },
            });
            return (
              <div key={id} className="pe2-fila">
                <div className="ancho pe2-nota">{s.sector.name}</div>
                <Campo etiqueta="Distancia máxima a un extintor" nota={quito ? 'Máximo 15 m (RTQ 7, 15.4)' : 'Máximo 20 m (AM 1257, Art. 120)'}>
                  <NumeroValor valor={v.distanciaExtintorM} unidad="m" disabled={ro} onChange={n => set({ distanciaExtintorM: n })} />
                </Campo>
                <Campo etiqueta="Recorrido máximo de evacuación" nota={quito ? 'RTQ 3: 6.3 (b) almacenamiento y 11.2 (b) industrial; además AM 1257, Art. 9 (25 m).' : 'Máximo 25 m (AM 1257, Art. 9).'}>
                  <NumeroValor valor={v.recorridoM} unidad="m" disabled={ro} onChange={n => set({ recorridoM: n })} />
                </Campo>
                <Campo etiqueta="¿Tiene rociadores automáticos?" nota="Se marca en el sector (capítulo 2).">
                  <input type="text" disabled readOnly value={rociadoresDe(s.sector, v) ? 'Sí' : 'No'} />
                </Campo>
                {quito && (
                  <Campo etiqueta="Nivel de riesgo según la RTQ 3" nota="RTQ 3, 6.3 (b) y 11.2 (b); niveles definidos en la RTQ 1 (definiciones de riesgo ligero, ordinario y alto).">
                    <select value={v.riesgoRtq3 ?? ''} disabled={ro} onChange={x => set({ riesgoRtq3: (x.target.value || null) as NivelRiesgoRtq3 | null })}>
                      <option value="">Elige…</option>
                      {NIVELES_RIESGO_RTQ3.map(n => <option key={n.valor} value={n.valor}>{n.etiqueta}</option>)}
                    </select>
                  </Campo>
                )}
              </div>
            );
          })}
        </>
      ))}

      {/* ── Amenazas y vulnerabilidad ── */}
      {tarjeta('amenaza_vulnerabilidad', (
        <>
          {e.amenazas.map((a, i) => {
            const r = (ev('amenaza_vulnerabilidad')?.resumen[a.id] ?? null) as { amenaza: Nivel4; vulnerabilidad: Nivel4; riesgo: Nivel4; porcentaje: number } | null;
            return (
              <div key={a.id} className="pe2-card pe2-sub-card">
                <div className="pe2-cabecera">
                  <h4>{a.nombre}</h4>
                  {!ro && a.tipo !== 'sismo' && <button type="button" className="pe2-btn peligro" onClick={() => setEntradas({ ...e, amenazas: e.amenazas.filter((_, j) => j !== i) })}>Quitar</button>}
                </div>
                <div className="pe2-grid">
                  {a.tipo === 'sismo' ? (
                    <Campo etiqueta="Amenaza (NEC-SE-DS, de los datos generales)" nota="Solo lectura: I = media; II a V = alta; VI = muy alta.">
                      <input type="text" disabled readOnly value={c.zona ? `Zona ${c.zona} → amenaza ${etiquetaNivel(amenazaDeZona(c.zona) as Nivel4, 'f')}${c.lugar ? ` (${c.lugar})` : ''}` : 'Falta la zona sísmica (capítulo 1)'} />
                    </Campo>
                  ) : (
                    <>
                      <Campo etiqueta="Nombre"><input type="text" value={a.nombre} disabled={ro} onChange={x => setAmenaza(i, { nombre: x.target.value })} /></Campo>
                      <Campo etiqueta="Nivel de la amenaza">
                        <select value={a.nivel ?? ''} disabled={ro} onChange={x => setAmenaza(i, { nivel: (x.target.value || null) as Nivel4 | null })}>
                          <option value="">Elige…</option>{NIVELES.map(n => <option key={n.v} value={n.v}>{n.t}</option>)}
                        </select>
                      </Campo>
                      <Campo etiqueta="Fuente (obligatoria)" nota="Mapa del IGEPN o PDOT del GAD. Sin fuente no se guarda.">
                        <input type="text" value={a.fuente ?? ''} disabled={ro} onChange={x => setAmenaza(i, { fuente: x.target.value })} />
                      </Campo>
                    </>
                  )}
                </div>
                <h4>Vulnerabilidad: criterios de preparación</h4>
                {(['personas', 'recursos', 'sistemas'] as const).map(dim => (
                  <div key={dim}>
                    <p className="pe2-nota" style={{ margin: '4px 0' }}>{ETIQUETA_DIMENSION[dim]}</p>
                    <div className="pe2-grid">
                      {criteriosDe(a.tipo).filter(cr => cr.dimension === dim).map(cr => {
                        const auto = verificacion ? respuestaDesdeVerificacion(cr.id, verificacion.trazas) : null;
                        return (
                          <Campo key={cr.id} etiqueta={cr.texto} nota={auto ? 'Desde la verificación normativa' : undefined}>
                            <select value={auto ?? a.respuestas[cr.id] ?? ''} disabled={ro || !!auto}
                              onChange={x => setAmenaza(i, { respuestas: { ...a.respuestas, [cr.id]: x.target.value as Respuesta } })}>
                              <option value="">Sin responder</option>{RESPUESTAS.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                            </select>
                          </Campo>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div className="pe2-grid">
                  <Campo etiqueta="Subir la vulnerabilidad (opcional)" nota="Solo se puede subir, nunca bajar, y con justificación.">
                    <select value={a.ajuste?.nivel ?? ''} disabled={ro}
                      onChange={x => setAmenaza(i, { ajuste: x.target.value ? { nivel: x.target.value as Nivel4, justificacion: a.ajuste?.justificacion ?? '' } : null })}>
                      <option value="">Sin ajuste</option>{NIVELES.map(n => <option key={n.v} value={n.v}>{n.t}</option>)}
                    </select>
                  </Campo>
                  {a.ajuste && (
                    <Campo etiqueta="Justificación (obligatoria)">
                      <input type="text" value={a.ajuste.justificacion} disabled={ro} onChange={x => setAmenaza(i, { ajuste: { ...a.ajuste!, justificacion: x.target.value } })} />
                    </Campo>
                  )}
                </div>
                {r && (
                  <div className="pe2-res">
                    <div><b>{formatoEc(r.porcentaje, 1)} %</b><span>Preparación</span></div>
                    <div><b>Amenaza {etiquetaNivel(r.amenaza, 'f')} × vulnerabilidad {etiquetaNivel(r.vulnerabilidad, 'f')}</b><span>Matriz 4×4 del plan de referencia</span></div>
                    <div><b>Riesgo {etiquetaNivel(r.riesgo, 'm')}</b><span>Resultado</span></div>
                  </div>
                )}
              </div>
            );
          })}
          {!ro && (
            <div className="pe2-acciones">
              {(['volcan', 'inundacion', 'otra'] as const).map(tipo => (
                <button key={tipo} type="button" className="pe2-btn" onClick={() => setEntradas({
                  ...e, amenazas: e.amenazas.concat({ id: `${tipo}-${Date.now()}`, tipo, nombre: ETIQUETA_AMENAZA[tipo], respuestas: {} }),
                })}>Agregar {ETIQUETA_AMENAZA[tipo].toLowerCase()}</button>
              ))}
            </div>
          )}
        </>
      ))}

      {!ro && !p.sinGuardar && (
        <div className="pe2-acciones">
          <button type="button" className="pe2-btn primario" onClick={p.onGuardar} disabled={p.guardando}>{p.guardando ? 'Guardando…' : 'Guardar cálculos'}</button>
        </div>
      )}
    </div>
  );
}
